import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import { Avatar, Box, IconButton, ListItemIcon, ListItemText, styled, Tooltip } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from "react-router-dom";
import UserTechAssisTrackingPerm from '../../enum/UserTechAssisTrackingPerm';
import UserType from '../../enum/UserType';
import CommentTask from '../../model/CommentTask';
import User from '../../model/User';
import { RootState } from '../../reducer';
import { logoutAction, successLoginAction } from "../../reducer/authentication";
import { changeThemeAction, useCbxTheme } from '../../reducer/theme';
import AuthService from "../../service/AuthService";
import { ServiceApi, WhereCondition } from '../../service/ServiceApi';
import { ServiceApi as ServiceApiWithLoading } from '../../service/ServiceApiWithLoading';
import UserApi from '../../service/UserApi';
import { getFirstLettersToAvatar } from '../../utils';
import eventEmitter from '../SafeDeskEventProvider';
import UserPhoto from '../UserPhoto';
import { loadingAction } from "./../../reducer/loading";
import ChangePasswordDialog, { ChangePasswordDialogData } from './ChangePasswordDialog';
import ChangePhotoDialog, { ChangePhotoDialogData } from './ChangePhotoDialog';
import ProfileMenu from './ProfileMenu';
import { useLanzacoinShop } from '../../context/LanzacoinShopContext';

import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BallotIcon from '@mui/icons-material/Ballot';
import DescriptionIcon from '@mui/icons-material/Description';
import EngineeringIcon from '@mui/icons-material/Engineering';
import FactoryIcon from '@mui/icons-material/Factory';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SavingsIcon from '@mui/icons-material/Savings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TaskSendEmailType from '../../enum/TaskSendEmailType';
import Task from '../../model/Task';
import TicketStatus from '../../model/TicketStatus';
import TaskApi from '../../service/TaskApi';
import HeaderLogo from '../HeaderLogo';
import taskStatusChangeCommentDialog from '../TaskStatusChangeCommentDialog';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import UserAdminType from '../../enum/UserAdminType';
import { hasAnyAdminPermission, hasAnyReportPermission, hasPermission } from '../../pages/System/Administration/Users/UserPermissions';
import { keyframes } from '@mui/system';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        '& p': {
            margin: 0
        }
    },
    grow: {
        flexGrow: 1,
    },
    menuButton: {
        textTransform: 'none',
        marginRight: theme.spacing(5),
    },
    header: {
        ...(theme.palette.mode === 'dark' ? { backgroundImage: 'linear-gradient(rgb(0, 57, 83), rgb(0, 57, 83))' } : { backgroundColor: '#ffffff' }),
        boxShadow: 'none',
        display: 'flex',
        height: 48,
        minHeight: 48
    },
    toolbar: {
        display: 'flex',
        alignItems: 'stretch',
        height: 48,
        minHeight: 48,
        padding: 0
    },
    logo: {
        margin: theme.spacing(0, 2),
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        width: 242,
        [theme.breakpoints.up('md')]: {
            //width: '15%',
        },
    },
    logoImg: {
        filter: (theme.palette.mode === 'dark' ? 'grayscale(100%) invert(100%) brightness(130%)' : undefined)
    },
    menuItem: {
        // height: 48,
        color: (theme.palette.mode === 'dark' ? 'white' : 'black'),
        paddingRight: 10,
        paddingLeft: 10
    },
    title: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },
    sectionDesktop: {
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'flex',
        },
    },
    sectionMobile: {
        display: 'flex',
        flex: 1,
        justifyContent: 'space-between',
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
    fontSize35: {
        fontSize: 35
    },
    avatar: {
        margin: theme.spacing(1),
        fontSize: 22
    },
    menuContent: {
        minWidth: '200px',
        padding: theme.spacing(2),
    },
    accountLogo: {
        width: 'auto',
        height: 'auto',
        maxWidth: 180,
        maxHeight: 48
    },
    accountLogoContainer: {
        width: 180,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'right',
        padding: '0 10px'
    },
    spanCounterComments: {
        position: 'absolute',
        color: 'white',
        left: '-5px',
        top: '-0',
        borderRadius: '50%',
        border: '1px solid rgb(105, 188, 238)',
        width: '20px',
        height: '20px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgb(4, 90, 142)',
        fontSize: '13px'
    },
    textColor: {
        color: (theme.palette.mode === 'dark' ? 'white' : 'black')
    }
}));

const glowAnimation = keyframes`
    0% {
        box-shadow: 0 0 4px 1px rgba(255, 215, 0, 0.4), 0 0 8px 2px rgba(255, 180, 0, 0.2);
    }
    50% {
        box-shadow: 0 0 8px 3px rgba(255, 215, 0, 0.7), 0 0 16px 6px rgba(255, 180, 0, 0.35);
    }
    100% {
        box-shadow: 0 0 4px 1px rgba(255, 215, 0, 0.4), 0 0 8px 2px rgba(255, 180, 0, 0.2);
    }
`;

function ThemeSelector() {
    const cbxTheme = useCbxTheme();
    const dispatch = useDispatch();

    function onChangeTheme(cbxTheme: string) {
        dispatch(changeThemeAction(cbxTheme === 'light' ? 'dark' : 'light'));
    }

    return <Tooltip placement="top" title={cbxTheme === 'light' ? 'Modo dark' : 'Modo light'}>
        <IconButton
            size='small'
            color="inherit"
            aria-label={cbxTheme === 'light' ? 'Modo dark' : 'Modo light'}
            onClick={() => onChangeTheme(cbxTheme)}
        >
            {cbxTheme === 'light' ? <Brightness4Icon style={{ fontSize: 34, color: 'black' }} /> : <Brightness7Icon style={{ fontSize: 34, color: 'white' }} />}
        </IconButton>
    </Tooltip>;
}

interface UserIconContentProps {
    user: User;
}

const commentTaskApi = new ServiceApi<CommentTask>('commentTask');
var idIntervalComments: NodeJS.Timer | null = null;

function CommentIconContent(props: UserIconContentProps) {
    const { user } = props;
    const [totalTask, setTotalTask] = useState<number>(0);
    const [totalTaskBot, setTotalTaskBot] = useState<number>(0);
    const [totalTaskYellow, setTotalTaskYellow] = useState<number>(0);
    const history = useHistory();

    const classes = useStyles();

    async function fetchTaskCounts(): Promise<{ total: number; totalYellow: number; totalBot: number }> {
        const where = getWhereConditions();
        const whereBot = getWhereConditionsBot();
        const whereYellow = getWhereConditionsYellow();

        const [total, totalYellow, totalBot] = await Promise.all([
            commentTaskApi.count({ where }),
            commentTaskApi.count({ where: whereYellow }),
            commentTaskApi.count({ where: whereBot }),
        ]);

        return { total, totalYellow, totalBot };
    }

    async function updateCountTotalTask() {
        try {
            clearIntervalCountTotalTask();

            const { total, totalYellow, totalBot } = await fetchTaskCounts();
            updateTotalTasks(total, totalYellow, totalBot);

            // Set up interval for periodic updates
            idIntervalComments = setInterval(async () => {
                try {
                    const { total: refreshedTotal, totalYellow: refreshedTotalYellow, totalBot: refreshedTotalBot } = await fetchTaskCounts();
                    updateTotalTasks(refreshedTotal, refreshedTotalYellow, refreshedTotalBot);
                } catch (error) {
                    console.error('Error updating task counts in interval:', error);
                }
            }, 120000);
        } catch (error) {
            console.error('Error updating task counts:', error);
        }
    }

    function getWhereConditions(): WhereCondition<CommentTask> {
        if (user.type === UserType.Commbox && user.perm_task === true) {
            return [['comment_task_users.user_id', '=', user.id], ['comment_task_users.awared_at', '=', null], ['canceled', '=', false], ['task.deleted_at', '=', null], ['user.name', '!=', 'LanzaBot']];
        } else if (user.type === UserType.FinalCustomer) {
            return [['is_public', '=', true], ['user.type', '=', UserType.Commbox], ['user.id', '!=', user.id], ['task.final_customer_id', '=', user.final_customer_id], ['viewed', '=', false], ['canceled', '=', false], ['task.deleted_at', '=', null], [`created_at`, '>=', '2023-12-15 00:00:00.000'], ['user.name', '!=', 'LanzaBot']];
        } else {
            return [['is_public', '=', true], ['user.type', '=', UserType.Commbox], ['user.id', '!=', user.id], ['task.final_customer.integrator_id', '=', user.integrator_id], ['viewed', '=', false], ['canceled', '=', false], ['task.deleted_at', '=', null], [`created_at`, '>=', '2023-12-15 00:00:00.000'], ['user.name', '!=', 'LanzaBot']];
        }
    }

    function getWhereConditionsBot(): WhereCondition<CommentTask> {
        if (user.type === UserType.Commbox && user.perm_task === true) {
            return [['comment_task_users.user_id', '=', user.id], ['comment_task_users.awared_at', '=', null], ['canceled', '=', false], ['task.deleted_at', '=', null], ['user.name', '=', 'LanzaBot']];
        } else if (user.type === UserType.FinalCustomer) {
            return [['is_public', '=', true], ['user.type', '=', UserType.Commbox], ['user.id', '!=', user.id], ['task.final_customer_id', '=', user.final_customer_id], ['viewed', '=', false], ['canceled', '=', false], ['task.deleted_at', '=', null], [`created_at`, '>=', '2023-12-15 00:00:00.000'], ['user.name', '=', 'LanzaBot']];
        } else {
            return [['is_public', '=', true], ['user.type', '=', UserType.Commbox], ['user.id', '!=', user.id], ['task.final_customer.integrator_id', '=', user.integrator_id], ['viewed', '=', false], ['canceled', '=', false], ['task.deleted_at', '=', null], [`created_at`, '>=', '2023-12-15 00:00:00.000'], ['user.name', '=', 'LanzaBot']];
        }
    }

    function getWhereConditionsYellow(): WhereCondition<CommentTask> {
        return [['comment_task_users.user_id', '=', user.id], ['comment_task_users.resolved_at', '=', null], ['canceled', '=', false], ['task.deleted_at', '=', null]];
    }

    function updateTotalTasks(total: number, totalYellow: number, totalBot: number) {
        setTotalTask(total);
        setTotalTaskYellow(totalYellow);
        setTotalTaskBot(totalBot);
    }

    function clearIntervalCountTotalTask() {
        if (idIntervalComments !== null) {
            clearInterval(idIntervalComments);
        }
    }

    useEffect(() => {
        eventEmitter.on('commentResolved', updateCountTotalTask);
        eventEmitter.on('commentAwared', updateCountTotalTask);
        updateCountTotalTask();
        return () => {
            clearIntervalCountTotalTask();
            eventEmitter.off('commentResolved', updateCountTotalTask);
            eventEmitter.off('commentAwared', updateCountTotalTask);
        };
    }, []);

    const titleYellow = totalTaskYellow === 0 ? (
        'Nenhum comentário não resolvido'
    ) : (
        `Você tem ${totalTaskYellow} comentário(s) não resolvido(s)`
    );

    const title = totalTask === 0 ? (
        'Nenhum comentário novo'
    ) : (
        `Você tem ${totalTask} comentário(s) novo(s)`
    );

    const titleBot = totalTaskBot === 0 ? (
        'Nenhum comentário novo (Bot)'
    ) : (
        `Você tem ${totalTaskBot} comentário(s) novo(s) (Bot)`
    );

    return <>
        <Tooltip className={classes.sectionDesktop} placement="top" title={title}>
            <IconButton
                size='small'
                color="inherit"
                aria-label={title}
                onClick={() => {
                    if (user.type === UserType.Commbox) {
                        if (user.perm_task === true) {
                            history.push('/system/task/newComments');
                        } else {
                            history.push('/system/support/newComments');
                        }
                    } else {
                        history.push('/support/tickets/newComments');
                    }
                }}
            >
                {totalTask > 0 ? <span className={classes.spanCounterComments}>{totalTask > 99 ? 99 : totalTask}</span> : null}
                <InsertCommentIcon style={{ color: (totalTask === 0 ? 'gray' : 'red'), fontSize: 34 }} />
            </IconButton>
        </Tooltip>
        {user.type === UserType.Commbox && user.perm_task === true ? <>
            <Tooltip className={classes.sectionDesktop} placement="top" title={titleBot}>
                <IconButton
                    size='small'
                    color="inherit"
                    aria-label={title}
                    onClick={() => {
                        history.push('/system/task/newCommentsBot');
                    }}
                >
                    {totalTaskBot > 0 ? <span className={classes.spanCounterComments}>{totalTaskBot > 99 ? 99 : totalTaskBot}</span> : null}
                    <InsertCommentIcon style={{ color: (totalTaskBot === 0 ? 'gray' : 'orange'), fontSize: 34 }} />
                </IconButton>
            </Tooltip>
            <Tooltip className={classes.sectionDesktop} placement="top" title={titleYellow}>
                <IconButton
                    size='small'
                    color="inherit"
                    aria-label={titleYellow}
                    onClick={() => {
                        history.push('/system/task/myObsPending');
                    }}
                >
                    {totalTaskYellow > 0 ? <span className={classes.spanCounterComments}>{totalTaskYellow > 99 ? 99 : totalTaskYellow}</span> : null}
                    <InsertCommentIcon style={{ color: (totalTaskYellow === 0 ? 'gray' : 'yellow'), fontSize: 34 }} />
                </IconButton>
            </Tooltip>
        </> : null}
    </>;
}

export interface IHeaderMenuItem {
    label: React.ReactNode;
    url: string;
    urlBase?: string;
    icon?: React.ReactNode;
}

const LogoContainer = styled('div')(({ theme }) => ({
    margin: theme.spacing(0, 2),
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'left',
    alignItems: 'center',
    /*width: '260px',
    [theme.breakpoints.down('lg')]: {
        display: 'none',
    }*/
}));

function Header() {

    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState<boolean>(false);
    const [changePhotoDialogOpen, setChangePhotoDialogOpen] = useState<boolean>(false);

    const { enqueueSnackbar } = useSnackbar();

    const dispatch = useDispatch();
    const { user, loggedIn } = useSelector(mapStateToProps);
    const { hasProfileHighlight } = useLanzacoinShop();

    const classes = useStyles();

    function handleProfileMenuOpen(event: React.MouseEvent<HTMLElement>) {
        setUserMenuAnchorEl(event.currentTarget);
    }

    function handleProfileMenuClose() {
        setUserMenuAnchorEl(null);
    }

    function loading(value: boolean) {
        dispatch(loadingAction(value));
    }

    function openChangePassword() {
        setChangePasswordDialogOpen(true);
        setUserMenuAnchorEl(null);
    }

    function openChangePhoto() {
        setChangePhotoDialogOpen(true);
        setUserMenuAnchorEl(null);
    }

    function closeChangePassword() {
        setChangePasswordDialogOpen(false);
    }

    function closeChangePhoto() {
        setChangePhotoDialogOpen(false);
    }

    async function logOut() {
        loading(true);
        await AuthService.instance.logout();
        loading(false);
        dispatch(logoutAction());
    }

    async function onSubmitChangePassowrd(data: ChangePasswordDialogData) {
        if (!user) return;
        const userApi = new UserApi();
        try {
            await userApi.changePassword(user.id, data.oldPassword, data.newPassword);
            enqueueSnackbar('Senha alterada com sucesso!', { variant: 'success' });
            closeChangePassword()
        } catch (e) {
            var errorMsg = e instanceof Error ? e.message : String(e);
            if (errorMsg.includes('Invalid Password')) {
                errorMsg = 'Senha invalida';
            }
            console.log(e);
            enqueueSnackbar(errorMsg, { variant: 'error' });
        }
    }

    async function onSubmitChangePhoto({ photo }: ChangePhotoDialogData) {
        if (!user || !(photo instanceof File)) return;
        const userApi = new UserApi();
        try {
            await userApi.uploadPhoto(photo, user.id);
            enqueueSnackbar('Foto alterada com sucesso!', { variant: 'success' });
            const userUpdated = await AuthService.instance.getAuthUser();
            dispatch(successLoginAction(userUpdated));
            closeChangePhoto();
        } catch (e) {
            var errorMsg = e instanceof Error ? e.message : String(e);
            console.log(e);
            enqueueSnackbar('Erro ao atualizar foto.', { variant: 'error' });
        }
    }

    function getMenuList(): IHeaderMenuItem[] {
        var listMenus: IHeaderMenuItem[] = [];
        if (!user) return listMenus;
        if (user.type === UserType.Commbox) {
            if (user.tech_assis_tracking_perm !== UserTechAssisTrackingPerm.YesExclusive && user.perm_order_received === true) {
                listMenus.push({ label: 'Encomendas', url: '/system/ordersReceived', icon: <LocalShippingIcon fontSize='medium' /> });
            }

            if (user.tech_assis_tracking_perm !== UserTechAssisTrackingPerm.YesExclusive && user.perm_tech_assis === true) {
                listMenus.push({ label: 'Assistências', url: '/system/technicalAssistance/all', urlBase: '/system/technicalAssistance', icon: <EngineeringIcon fontSize='medium' /> });
            }

            if ([UserTechAssisTrackingPerm.Yes, UserTechAssisTrackingPerm.YesExclusive].includes(user.tech_assis_tracking_perm)) {
                listMenus.push({ label: 'Rastreio', url: '/system/techAssisTracking', icon: <GpsFixedIcon fontSize='medium' /> });
            }

            if (user.tech_assis_tracking_perm !== UserTechAssisTrackingPerm.YesExclusive) {
                // listMenus.push({ label: 'Suporte', url: '/system/support/open', urlBase: '/system/support', icon: <SupportAgentIcon fontSize='medium' /> });
                listMenus.push({ label: 'Minhas Solicitações', url: '/system/prodEng/open', urlBase: '/system/prodEng', icon: <IntegrationInstructionsIcon fontSize='medium' /> });
                listMenus.push({ label: 'Clientes', url: '/system/clients', urlBase: '/system/clients', icon: <PeopleAltIcon fontSize='medium' /> });
                if (user.perm_production === true) {
                    listMenus.push({ label: 'Produção', url: '/system/production/all', urlBase: '/system/production', icon: <FactoryIcon fontSize='medium' /> });
                }
                if (user.perm_task === true) {
                    listMenus.push({ label: 'Tarefas', url: '/system/task/myOpen', urlBase: '/system/task', icon: <AssignmentIcon fontSize='medium' /> });
                }

                // Só exibe o menu admin se tem pelo menos uma permissão
                if (hasAnyAdminPermission(user.permissions)) {
                    listMenus.push({ label: 'Admin', url: '/system/administration', icon: <ManageAccountsIcon fontSize='medium' /> });
                }

                if (hasAnyReportPermission(user.permissions)) {
                    listMenus.push({ label: 'Relatórios', url: '/system/reports', icon: <AssessmentIcon fontSize='medium' /> });
                }
                if (user.perm_task === true) {
                    listMenus.push({ label: 'Atividades', url: '/system/activities', icon: <BallotIcon fontSize='medium' /> });
                }
                listMenus.push({ label: 'Ocorrências', url: '/system/ocurrence', icon: <AppRegistrationIcon fontSize='medium' /> });
                listMenus.push({ label: 'Documentação', url: '/system/documentation', icon: <DescriptionIcon fontSize='medium' /> });
                if (user?.admin_type === UserAdminType.SuperAdmin) {
                    listMenus.push({ label: 'Projetos', url: '/system/projectsDashboard', icon: <DashboardIcon fontSize='medium' /> });
                }
                if (hasPermission(user.permissions, 'admin.licenses')) {
                    listMenus.push({ label: 'Licenças', url: '/system/licensesDashboard', icon: <VpnKeyIcon fontSize='medium' /> });
                }
            }
        }

        return listMenus;
    }

    const isMenuOpen = Boolean(userMenuAnchorEl);
    const url = window.location.hash;
    const menus = getMenuList();

    if (!user) return null;

    return (
        <div className={classes.root} >
            <AppBar position="static" className={classes.header}>
                <Toolbar className={classes.toolbar}>
                    <LogoContainer>
                        <HeaderLogo />
                    </LogoContainer>
                    <Box sx={{ display: 'flex', overflow: 'auto' }}>
                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                            {menus.map((item, i) => (
                                <MenuItem
                                    className={classes.menuItem}
                                    key={i}
                                    selected={!!((item.urlBase && url.includes(item.urlBase)) || (item.url && url.includes(item.url)))}
                                    to={item.url}
                                    component={Link}>
                                    <ListItemIcon style={{ minWidth: 28 }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText>{item.label}</ListItemText>
                                </MenuItem>
                            ))}
                        </div>
                    </Box>

                    <div className={[classes.sectionDesktop, classes.textColor].join(' ')} style={{ flex: 1, justifyContent: 'end', alignItems: 'center', paddingRight: 10, overflow: 'hidden' }}>
                        {(user.type === UserType.Commbox && user.perm_task === true) && <CurrentTaskService user={user} />}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', position: 'relative', alignItems: 'center', paddingRight: 8 }}>
                        {(user.perm_task === true || user.type !== UserType.Commbox) && <CommentIconContent user={user} />}
                        <Avatar
                            style={{ cursor: 'pointer' }}
                            className={classes.avatar}
                            onClick={handleProfileMenuOpen}
                            sx={hasProfileHighlight ? {
                                border: '2px solid #FFD700',
                                animation: `${glowAnimation} 2s ease-in-out infinite`,
                                borderRadius: '50%'
                            } : undefined}
                        >
                            {user.photo ? <UserPhoto key={user.photo} id={user.id} photo={user.photo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getFirstLettersToAvatar(user.name)}
                        </Avatar>
                    </div>
                </Toolbar>
            </AppBar>
            <ProfileMenu
                user={user}
                isOpen={isMenuOpen}
                anchorEl={userMenuAnchorEl}
                onClose={handleProfileMenuClose}
                onLogOut={logOut}
                openChangePassword={openChangePassword}
                openChangePhoto={openChangePhoto}
            />
            <ChangePasswordDialog
                changePasswordDialogOpen={changePasswordDialogOpen}
                onCancel={closeChangePassword}
                onSubmit={onSubmitChangePassowrd}
            />
            <ChangePhotoDialog
                changePhotoDialogOpen={changePhotoDialogOpen}
                onCancel={closeChangePhoto}
                onSubmit={onSubmitChangePhoto}
            />
        </div>
    );

}

const taskApi = new ServiceApi<Task>('task');
const taskApiWithLoading = new TaskApi();
const ticketStatusApi = new ServiceApiWithLoading<TicketStatus>('ticketStatus');

var idIntervalCurrentTaskService: NodeJS.Timer | null = null;

interface CurrentTaskServiceProps {
    user: User;
}

function CurrentTaskService({ user }: CurrentTaskServiceProps) {

    const history = useHistory();
    const [currentTaskservice, setCurrentTaskService] = useState<Task | null>(null);

    function clearIntervalCurrentTaskService() {
        if (idIntervalCurrentTaskService !== null) {
            clearInterval(idIntervalCurrentTaskService);
        }
    }

    function refreshCurrentTaskService() {
        clearIntervalCurrentTaskService();
        const where: WhereCondition<Task> = [];
        where.push(['user_id', '=', user.id]);
        where.push(['ticket_status.name', '=', 'Atendimento']);
        return taskApi.getList({ where, limit: 1, order: { 'last_update_status': 'ASC' } }).then((t) => {
            setCurrentTaskService(t.length > 0 ? t[0] : null);
            idIntervalCurrentTaskService = setInterval(() => {
                taskApi.getList({ where, limit: 1, order: { 'last_update_status': 'ASC' } }).then((t2) => {
                    setCurrentTaskService(t2.length > 0 ? t2[0] : null);
                });
            }, 120000);
        });
    }

    useEffect(() => {
        eventEmitter.on('taskSaved', refreshCurrentTaskService);
        refreshCurrentTaskService();
        return () => {
            clearIntervalCurrentTaskService();
            eventEmitter.off('taskSaved', refreshCurrentTaskService);
        };
    }, []);

    async function onClickPauseTask() {
        const ticketStatusList = await ticketStatusApi.getList({ where: [['name', '=', 'Pausado (temporariamente)']], limit: 1, relations: ['require_task_comment_user'] });
        const ticketStatusAtendimentoList = await ticketStatusApi.getList({ where: [['name', '=', 'Atendimento']], limit: 1 });
        if (ticketStatusList && ticketStatusList.length > 0 && ticketStatusAtendimentoList && ticketStatusAtendimentoList.length > 0 && currentTaskservice) {
            //buscar antes pra ver se ainda está em atendimento
            const ticketStatusAtendimento = ticketStatusAtendimentoList[0];
            const ticketStatusPausado = ticketStatusList[0];
            if (ticketStatusAtendimento.id === currentTaskservice.ticket_status_id) {
                if (ticketStatusPausado.require_task_comment)
                    taskStatusChangeCommentDialog({ onProceed: () => pauseTask(ticketStatusPausado), user_id: ticketStatusPausado.require_task_comment_user?.id, user_name: ticketStatusPausado.require_task_comment_user?.name, task_id: currentTaskservice?.id || 0, auth_user_id: user.id });
                else
                    await pauseTask(ticketStatusPausado);
            }
        }
    }

    async function pauseTask(ticketStatusPausado: TicketStatus) {
        if (currentTaskservice) {
            await taskApiWithLoading.update(currentTaskservice.id, { ticket_status_id: ticketStatusPausado.id });
            await taskApiWithLoading.sendEmail(currentTaskservice.id, TaskSendEmailType.StatusChangeObserver, user.id, currentTaskservice.ticket_status_id);
            eventEmitter.emit('taskSaved', currentTaskservice);
        }
    }

    return <>{currentTaskservice ? <>
        <div title={currentTaskservice.name} onClick={() => history.push(currentTaskservice.project_id ? `/system/task/project/${currentTaskservice.project_id}/task/${currentTaskservice.id}` : `/system/task/${currentTaskservice.id}`)} style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 34px)', textAlign: 'right' }}>{currentTaskservice.name}</div>
        <Tooltip placement="top" title={'Pausar a tarefa'}>
            <IconButton
                size='small'
                color="inherit"
                aria-label={'Pausar a tarefa'}
                onClick={() => onClickPauseTask()}
            >
                <PauseCircleOutlineIcon fontSize='medium' />
            </IconButton>
        </Tooltip>
    </>
        : <div onClick={() => history.push(`/system/task/myOpen`)} style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', textAlign: 'right' }}>👀 Não há nada em atendimento</div>}</>;
}

function mapStateToProps(state: RootState) {
    const { loggedIn, user } = state.authentication;
    return {
        loggedIn,
        user
    };
}

export default Header;